import { lazy, Suspense, useEffect, useState } from 'react'
import { Link, NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { Bot, BookOpen, ChartNoAxesColumnIncreasing, CircleDot, Crown, Gamepad2, History, LogOut, Menu, Settings, SlidersHorizontal, Swords, X } from 'lucide-react'
import { api, setCsrf } from './api.js'
import SetupPage from './pages/SetupPage.jsx'
const HomePage = lazy(() => import('./pages/HomePage.jsx'))
const GamePage = lazy(() => import('./pages/GamePage.jsx'))
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage.jsx'))
const LearnPage = lazy(() => import('./pages/LearnPage.jsx'))
const HistoryPage = lazy(() => import('./pages/HistoryPage.jsx'))
const AdminPage = lazy(() => import('./pages/AdminPage.jsx'))
const GamesPage = lazy(() => import('./pages/GamesPage.jsx'))
const SettingsPage = lazy(() => import('./pages/SettingsPage.jsx'))
const CheckersGamePage = lazy(() => import('./pages/CheckersGamePage.jsx'))
const MinesweeperPage = lazy(() => import('./pages/MinesweeperPage.jsx'))
const MinesweeperGamePage = lazy(() => import('./pages/MinesweeperGamePage.jsx'))
const FoxesPage = lazy(() => import('./pages/FoxesPage.jsx'))
const FoxesGamePage = lazy(() => import('./pages/FoxesGamePage.jsx'))
const FourPage = lazy(() => import('./pages/FourPage.jsx'))
const FourGamePage = lazy(() => import('./pages/FourGamePage.jsx'))
const NavalPage = lazy(() => import('./pages/NavalPage.jsx'))
const NavalGamePage = lazy(() => import('./pages/NavalGamePage.jsx'))

export default function App() {
  const location = useLocation()
  const [status, setStatus] = useState(null)
  const [me, setMe] = useState(null)
  const [error, setError] = useState('')

  async function load() {
    try {
      const next = await api('/api/status')
      setStatus(next)
      if (next.configured && next.authenticated) {
        const profile = await api('/api/me')
        setCsrf(profile.csrf); setMe(profile)
      }
    } catch (err) { setError(err.message) }
  }

  useEffect(() => { load() }, [])
  useEffect(() => {
    if (!status?.configured || !status.authenticated) return
    api('/api/me').then((profile) => { setCsrf(profile.csrf); setMe(profile) }).catch((err) => setError(err.message))
  }, [location.pathname, status?.configured, status?.authenticated])
  useEffect(() => {
    if (!me) return
    const source = new EventSource('/api/session/events')
    source.addEventListener('logout', () => { source.close(); window.location.assign('/?loggedOut=1') })
    return () => source.close()
  }, [me])

  if (error) return <Centered><div className="error-card"><Crown size={34}/><h1>Couldn’t open the board</h1><p>{error}</p><button onClick={() => window.location.reload()}>Try again</button></div></Centered>
  if (!status) return <Centered><div className="brand-loader"><Crown/><span>Preparing the board…</span></div></Centered>
  if (!status.configured) return <Routes><Route path="*" element={<SetupPage onComplete={load} />} /></Routes>
  if (!status.authenticated || !me) return <SignedOut />
  return <Shell me={me}><Suspense fallback={<div className="page-loader">Setting the pieces…</div>}><Routes>
    <Route path="/" element={<GamesPage me={me} />} />
    <Route path="/chess" element={<HomePage me={me} />} />
    <Route path="/game/:id" element={<GamePage me={me} />} />
    <Route path="/checkers" element={<HomePage me={me} gameType="checkers" />} />
    <Route path="/checkers/game/:id" element={<CheckersGamePage me={me} />} />
    <Route path="/minesweeper" element={<MinesweeperPage me={me} />} />
    <Route path="/minesweeper/game/:id" element={<MinesweeperGamePage me={me} />} />
    <Route path="/foxes" element={<FoxesPage me={me} />} />
    <Route path="/foxes/game/:id" element={<FoxesGamePage me={me} />} />
    <Route path="/four" element={<FourPage me={me} />} />
    <Route path="/four/game/:id" element={<FourGamePage me={me} />} />
    <Route path="/naval" element={<NavalPage me={me} />} />
    <Route path="/naval/game/:id" element={<NavalGamePage me={me} />} />
    <Route path="/leaderboard" element={<LeaderboardPage me={me} />} />
    <Route path="/learn" element={<LearnPage />} />
    <Route path="/history" element={<HistoryPage />} />
    <Route path="/settings" element={<SettingsPage me={me} />} />
    <Route path="/admin" element={me.isAdmin ? <AdminPage /> : <Navigate to="/" replace />} />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes></Suspense></Shell>
}

function Centered({ children }) { return <main className="centered-page">{children}</main> }

function SignedOut() {
  const location = useLocation()
  const loggedOut = new URLSearchParams(location.search).has('loggedOut')
  return <main className="welcome-page">
    <div className="welcome-art" aria-hidden="true"><div className="orb orb-one"/><div className="orb orb-two"/><Gamepad2 className="hero-crown" strokeWidth={1}/></div>
    <section className="welcome-copy"><div className="eyebrow">ONE ACCOUNT · MANY GAMES · ADAPTIVE AI</div><h1><span>Pick a game.</span><span>Make it yours.</span><em><span>Challenge the</span><span>machine.</span></em></h1><p>Games w/AI brings configurable opponents, genuine ratings, and personal game styles into one evolving library. Six 3D games are ready now.</p>{loggedOut && <div className="notice">You’ve been signed out.</div>}<a className="button primary large" href="/auth/login"><Swords size={19}/> Sign in to play</a><Link className="text-link" to="/learn">Explore all game rules →</Link></section>
  </main>
}

function Shell({ me, children }) {
  const [open, setOpen] = useState(false)
  const links = [
    ['/', Gamepad2, 'Games'], ['/chess', Swords, 'Chess'], ['/checkers', CircleDot, 'Checkers'], ['/leaderboard', ChartNoAxesColumnIncreasing, 'Leaderboard'], ['/learn', BookOpen, 'How to play'], ['/history', History, 'History']
  ]
  async function logout() { const result = await api('/auth/logout', { method: 'POST' }); window.location.assign(result.redirect) }
  return <div className="app-shell">
    <header className="topbar"><Link to="/" className="brand"><span className="brand-mark"><Gamepad2 size={21}/></span><span>Games <i>w/AI</i></span></Link><nav className={open ? 'nav open' : 'nav'}>{links.map(([to, Icon, label]) => <NavLink key={to} to={to} end={to === '/'} onClick={() => setOpen(false)}><Icon size={17}/>{label}</NavLink>)}{me.isAdmin && <NavLink to="/admin" onClick={() => setOpen(false)}><Settings size={17}/>Admin</NavLink>}</nav><div className="account"><Link to="/settings" className="account-link" aria-label="Personal game settings"><div className="avatar">{me.user.avatar ? <img src={me.user.avatar} alt=""/> : me.user.displayName.slice(0, 1).toUpperCase()}</div><div className="account-copy"><b>{me.user.displayName}</b><span>{me.player.ratings.rapid.rating} chess rapid</span></div><SlidersHorizontal size={15}/></Link><button className="icon-button" onClick={logout} aria-label="Sign out"><LogOut size={18}/></button></div><button className="mobile-menu icon-button" onClick={() => setOpen(!open)} aria-label="Toggle navigation">{open ? <X/> : <Menu/>}</button></header>
    <main className="page">{children}</main>
    <footer><span>Games w/AI</span><span><Bot size={14}/> Every model action is validated by its game’s server-side rules.</span></footer>
  </div>
}
